import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import LoadingScreen from "@/components/LoadingScreen";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";

export default function Exam() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [credits, setCredits] = useState(0);

  useEffect(() => {
    // exam questions are only returned at generation time; we also have history,
    // but for submission flow we re-use state held in browser. If user reloads,
    // they will need to regenerate. We fetch credits balance only here.
    api.get("/credits/balance").then(({ data }) => setCredits(data.credits)).catch(() => {});

    // attempt to reconstruct from sessionStorage (set on generation)
    const stored = sessionStorage.getItem(`exam:${examId}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      setExam(parsed);
      setAnswers(Array(parsed.questions.length).fill(-1));
    } else {
      // fall back: no questions — must regenerate. Inform user.
      setExam({ topic: "Exam", questions: [] });
    }
  }, [examId]);

  const submit = async () => {
    if (answers.some((a) => a < 0)) { toast.error("Answer every question"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/exam/submit", { exam_id: examId, answers });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!exam) return <LoadingScreen />;

  return (
    <div className="min-h-screen" data-testid="exam-page">
      <Header credits={credits} onOpenAd={() => {}} onOpenCheckout={() => {}} />
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-10 space-y-6">
        <div>
          <div className="label-eyebrow mb-2">Adaptive Exam</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">{exam.topic}</h1>
          <p className="text-white/50 text-sm mt-1 font-mono-oak">exam · {examId}</p>
        </div>

        {exam.questions?.length === 0 && (
          <div className="glass p-6" data-testid="exam-expired">
            <div className="font-display text-xl">This exam session has expired.</div>
            <p className="text-white/55 text-sm mt-2">Exam questions live in this tab only. Generate a new exam from the dashboard.</p>
            <button
              onClick={() => exam.path_id ? navigate(`/path/${exam.path_id}`) : navigate("/dashboard")}
              className="btn-primary mt-4 rounded-lg px-4 py-2 text-sm"
              data-testid="btn-return-to-course-expired"
            >
              {exam.path_id ? "Return to Course" : "Back to dashboard"}
            </button>
          </div>
        )}

        {!result && exam.questions?.length > 0 && (
          <div className="space-y-5">
            {exam.questions.map((q, i) => (
              <div key={i} className="glass p-6" data-testid={`question-${i}`}>
                <div className="flex items-start gap-3 mb-4">
                  <span className="label-eyebrow bg-white/5 px-2 py-0.5 rounded-full !text-[10px] mt-1">Q{i + 1}</span>
                  <div className="font-display text-lg md:text-xl tracking-tight leading-snug">{q.q}</div>
                </div>
                <div className="space-y-2">
                  {q.options.map((o, j) => {
                    const picked = answers[i] === j;
                    return (
                      <button
                        key={j}
                        onClick={() => setAnswers((a) => a.map((v, idx) => (idx === i ? j : v)))}
                        className={`w-full text-left px-4 py-3 rounded-lg border transition text-sm ${picked ? "bg-[#0044FF]/15 border-[#0044FF]/45 text-white" : "bg-white/[0.03] border-white/10 text-white/75 hover:bg-white/[0.06]"}`}
                        data-testid={`q${i}-opt${j}`}
                      >
                        <span className="font-mono-oak text-xs text-white/40 mr-2">{String.fromCharCode(65 + j)}.</span>
                        {o}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <button onClick={submit} disabled={submitting} className="btn-primary rounded-xl px-6 py-3 font-display font-semibold disabled:opacity-50" data-testid="btn-submit-exam">
                {submitting ? "Grading…" : "Submit exam"} <ArrowRight size={14} className="inline ml-1" />
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-5" data-testid="exam-result">
            <div className={`glass p-6 ${result.score >= 60 ? "glow-mint" : ""}`}>
              <div className="label-eyebrow">Your score</div>
              <div className="font-display text-5xl md:text-6xl tracking-tight mt-1">{result.score}<span className="text-xl text-white/50">%</span></div>
              <div className="text-white/55 text-sm mt-1">{result.correct} of {result.total} correct</div>
            </div>
            <div className="space-y-4">
              {result.review.map((r, i) => (
                <div key={i} className="glass p-5" data-testid={`review-${i}`}>
                  <div className="flex items-start gap-2">
                    {r.is_right ? <CheckCircle2 className="text-[#00FF9D] mt-1" size={18} /> : <XCircle className="text-rose-400 mt-1" size={18} />}
                    <div className="flex-1">
                      <div className="font-display text-base md:text-lg">{r.q}</div>
                      <ul className="mt-2 space-y-1 text-sm">
                        {r.options.map((o, j) => (
                          <li
                            key={j}
                            className={`px-3 py-1.5 rounded-md border ${j === r.correct ? "bg-[#00FF9D]/10 border-[#00FF9D]/30 text-white" : j === r.chosen ? "bg-rose-500/10 border-rose-400/30 text-white/80" : "border-transparent text-white/55"}`}
                          >
                            {String.fromCharCode(65 + j)}. {o}
                          </li>
                        ))}
                      </ul>
                      {r.explanation && <div className="text-white/55 text-xs mt-2 italic">{r.explanation}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => exam.path_id ? navigate(`/path/${exam.path_id}`) : navigate("/dashboard")}
              className="btn-primary rounded-xl px-5 py-3 text-sm font-display"
              data-testid="btn-return-to-course"
            >
              {exam.path_id ? "Return to Course" : "Back to dashboard"}
            </button>
          </div>
        )}
      </main>
      <Footer />
      <BottomNav />
    </div>
  );
}
